<template>
  <Modal class="join-session" @close="cancel" v-if="modals.joinSession">
    <h3>{{ $t("session.joinSession") }}</h3>
    <div class="form-group">
      <label>{{ $t("session.enterChannelJoin") }}</label>
      <input
        type="text"
        v-model="sessionInput"
        :placeholder="$t('session.sessionIdPlaceholder')"
        @keyup.enter="confirm"
        ref="sessionInput"
      />
    </div>
    <div class="button-group">
      <div 
        class="button confirm-btn" 
        @click="confirm"
        @touchend.prevent="handleTouchConfirm"
      >
        <font-awesome-icon icon="check" />
        {{ $t("common.confirm") }}
      </div>
      <div 
        class="button cancel-btn" 
        @click="cancel"
        @touchend.prevent="handleTouchCancel"
      >
        <font-awesome-icon icon="times" />
        {{ $t("common.cancel") }}
      </div>
    </div>
  </Modal>
</template>

<script>
import Modal from "./Modal";
import { mapMutations, mapState } from "vuex";

export default {
  components: {
    Modal,
  },
  data() {
    return {
      sessionInput: "",
      touchHandled: false, // Prevent double-firing on mobile
    };
  },
  computed: {
    ...mapState(["modals"]),
  },
  methods: {
    confirm() {
      console.log("[JoinSession] Confirm called, input:", this.sessionInput);
      
      if (!this.sessionInput.trim()) {
        console.log("[JoinSession] Empty input, returning");
        return;
      }

      try {
        let sessionId = this.sessionInput.trim();
        console.log("[JoinSession] Processing session ID:", sessionId);
        
        // Extract session ID from URL if needed
        if (sessionId.match(/^https?:\/\//i)) {
          sessionId = sessionId.split("#").pop();
          console.log("[JoinSession] Extracted ID from URL:", sessionId);
        }

        console.log("[JoinSession] Emitting confirm event");
        this.$emit("confirm", sessionId);
        this.sessionInput = "";
        this.toggleModal("joinSession");
        console.log("[JoinSession] Join process completed");
      } catch (error) {
        console.error("[JoinSession] Error during confirm:", error);
      }
    },
    cancel() {
      console.log("[JoinSession] Cancel called");
      this.sessionInput = "";
      this.toggleModal("joinSession");
    },
    handleTouchConfirm() {
      console.log("[JoinSession] Touch confirm event");
      if (this.touchHandled) return;
      this.touchHandled = true;
      this.confirm();
      // Reset flag after a short delay
      setTimeout(() => {
        this.touchHandled = false;
      }, 300);
    },
    handleTouchCancel() {
      console.log("[JoinSession] Touch cancel event");
      if (this.touchHandled) return;
      this.touchHandled = true;
      this.cancel();
      setTimeout(() => {
        this.touchHandled = false;
      }, 300);
    },
    ...mapMutations(["toggleModal"]),
  },
  mounted() {
    console.log("[JoinSession] Modal mounted");
    // Focus input when modal opens, but avoid on mobile to prevent keyboard issues
    this.$nextTick(() => {
      if (this.$refs.sessionInput) {
        // Only auto-focus on desktop (not on touch devices)
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (!isTouchDevice) {
          this.$refs.sessionInput.focus();
        }
      }
    });
  },
};
</script>

<style lang="scss">
.join-session {
  .top-right-buttons {
    display: none !important;
  }

  .modal {
    max-width: 400px !important;
    min-width: 300px;
    padding: 20px 25px !important;
  }

  h3 {
    display: none !important;
  }

  .form-group {
    margin-bottom: 15px;

    label {
      display: block;
      margin-bottom: 10px;
      font-size: 17px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.95);
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      letter-spacing: 0.3px;
    }

    input {
      width: 100%;
      padding: 14px 18px;
      font-size: 16px;
      border: 2px solid transparent;
      border-radius: 12px;
      background: rgba(255, 255, 255, 0.98);
      color: #2c3e50;
      box-sizing: border-box;
      transition: all 0.3s ease;
      font-weight: 500;
      text-align: center;

      &:focus {
        outline: none;
        background: white;
        border-color: #4a90e2;
        box-shadow: 0 0 0 4px rgba(74, 144, 226, 0.15),
                    0 4px 12px rgba(0, 0, 0, 0.1);
        transform: translateY(-1px);
      }

      &::placeholder {
        color: #95a5a6;
        font-weight: 400;
      }
    }
  }

  .button-group {
    display: flex;
    justify-content: center;
    gap: 8px;

    .button {
      flex: 1;
      min-width: 80px;
      padding: 6px 16px;
      cursor: pointer;
      font-size: 13px;
      font-weight: 500;
      border-radius: 20px;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      border: none;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;

      &.confirm-btn {
        background: #007bff;
        color: white;

        &:hover {
          background: #0069d9;
          box-shadow: 0 3px 6px rgba(0, 123, 255, 0.25);
        }

        &:active {
          background: #0056b3;
          transform: scale(0.98);
        }
      }

      &.cancel-btn {
        background: #dc3545;
        color: white;

        &:hover {
          background: #c82333;
          box-shadow: 0 3px 6px rgba(220, 53, 69, 0.25);
        }

        &:active {
          background: #bd2130;
          transform: scale(0.98);
        }
      }

      svg {
        width: 12px;
        flex-shrink: 0;
      }
    }
  }
}
</style>
